import React from 'react';

import type { SimpleObject } from '@clearverify/types';

import { ModalId } from 'src/constants/modals';
import { ModalArgs, ModalState } from 'src/providers/ModalsProvider';

import { useModalsContext } from './useModalsContext';

export const useModal = <TArgs extends SimpleObject>(id?: ModalId) => {
  const { openModal, closeModal, state } = useModalsContext();

  const [isClosureAllowed, setIsClosureAllowed] = React.useState(true);

  const modalState = React.useMemo((): ModalState<TArgs> => {
    return id ? { ...defaultModalState, ...state[id] } : defaultModalState;
  }, [id, state]);

  const openCurrentModal = React.useCallback(
    (customId?: ModalId, customArgs?: ModalArgs<TArgs>) => {
      if (customId) {
        openModal(customId, customArgs);
      } else if (id) {
        openModal(id);
      }
    },
    [id, openModal],
  );

  const closeCurrentModal = React.useCallback(
    (customId?: ModalId) => {
      if (isClosureAllowed) {
        closeModal(String(customId ?? id));
      }
    },
    [closeModal, id, isClosureAllowed],
  );

  return {
    ...modalState,
    openModal: openCurrentModal,
    closeModal: closeCurrentModal,
    switchClosability: setIsClosureAllowed,
  };
};

const defaultModalState: ModalState<any> = {
  isOpen: false,
  args: null,
};
